import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Star, ShieldAlert, ChevronLeft, ChevronRight, Download, Upload, Power, PowerOff, Filter } from "lucide-react";
import DuplicateGuestPanel, { recordCreateNewDecision } from "@/components/duplicate/DuplicateGuestPanel";
import { DatePicker } from "@/components/ui/date-picker";
import { useCurrency } from "@/lib/currency";

const initialForm = { title_code: "MR", first_name: "", last_name: "", gender_code: "", dob: "",
  nationality_code: "", primary_phone: "", email: "", classification_code: "", vip_status: "none" };

const lookupCache = {};
async function loadLookup(key) {
  if (lookupCache[key]) return lookupCache[key];
  try { const { data } = await api.get(`/master/${key}/?page_size=500`);
    const map = {}; data.items.forEach(r => { map[r.code] = r.name; });
    lookupCache[key] = map; return map;
  } catch { return {}; }
}

export default function Guests() {
  const money = useCurrency();
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState(""); const [vip, setVip] = useState(""); const [bl, setBl] = useState("");
  const [active, setActive] = useState(""); const [nat, setNat] = useState(""); const [classification, setClassification] = useState("");
  const [page, setPage] = useState(1); const [pageSize] = useState(25);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [dupDialog, setDupDialog] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(null); // 'activate' | 'deactivate'
  const [lookups, setLookups] = useState({ countries: {}, titles: {}, classifications: {} });
  const fileRef = useRef(null);

  useEffect(() => { (async () => {
    setLookups({ countries: await loadLookup("countries"), titles: await loadLookup("titles"),
      classifications: await loadLookup("guest-classifications"),
      genders: await loadLookup("genders") });
  })(); }, []);

  const load = async () => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (q) params.set("q", q);
    if (vip) params.set("vip_status", vip);
    if (bl) params.set("is_blacklisted", bl);
    try { const { data } = await api.get(`/pms/guests/?${params}`); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [page, q, vip, bl]);

  const filteredItems = data.items.filter(g =>
    (!active || (active === "true" ? g.is_active : !g.is_active)) &&
    (!nat || g.nationality_code === nat) &&
    (!classification || g.classification_code === classification)
  );

  const submit = async ({ force = false, matches = [], reason = null } = {}) => {
    try {
      const url = force ? "/pms/guests/force" : "/pms/guests/";
      const payload = force
        ? { ...form,
            possible_duplicate_of: matches.map(m => m.id),
            duplicate_reason: reason }
        : form;
      const { data } = await api.post(url, payload);
      toast.success("Guest created");
      // PR2-2 — record the decision after the create succeeds
      if (force && matches.length && data?.id) {
        await recordCreateNewDecision({
          source: "guest_form", matches, newGuestId: data.id,
          reason, payloadSnapshot: form,
        });
      }
      setOpen(false); setDupDialog(null); setForm(initialForm); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.matches) setDupDialog({ matches: d.matches });
      else toast.error(formatApiError(e));
    }
  };

  const onPanelDecision = (decision, matches, extras = {}) => {
    if (decision === "continue_with_existing") {
      setOpen(false); setForm(initialForm);
    } else if (decision === "open_existing") {
      // handled inside the panel
    } else if (decision === "create_new_with_reason") {
      submit({ force: true, matches, reason: extras.reason });
    } else if (decision === "ignore") {
      // Ignore — do nothing, user proceeds with normal Create button
    }
  };

  const toggleAll = (v) => setSelected(v ? new Set(filteredItems.map(g => g.id)) : new Set());
  const toggleOne = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q); if (vip) params.set("vip_status", vip);
    window.open(`${API_BASE}/pms/guests/export.csv?${params}`, "_blank");
  };

  const importCsv = async () => {
    if (!importFile) return toast.error("Choose a CSV file first");
    const fd = new FormData(); fd.append("file", importFile);
    try {
      const { data } = await api.post("/pms/guests/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} guests · ${data.failed.length} failed`);
      setImportOpen(false); setImportFile(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doBulk = async (kind) => {
    const url = kind === "activate" ? "/pms/guests/bulk-activate" : "/pms/guests/bulk-deactivate";
    try {
      const { data } = await api.post(url, { ids: [...selected] });
      toast.success(`${data.updated} guests ${kind}d`);
      setSelected(new Set()); setBulkConfirm(null); load();
    } catch (e) { toast.error(formatApiError(e)); setBulkConfirm(null); }
  };

  const bulkExportSelected = () => {
    if (!selected.size) return toast.error("Select rows first");
    // For now exports all visible filtered guests via the same endpoint
    exportCsv();
  };

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  return (
    <div data-testid="guests-page">
      <PageHeader title="Guests" subtitle="Guest Profile Master"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} className="h-9 px-3 text-[13px] bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="import-guests-button"><Upload className="w-3.5 h-3.5 mr-1.5"/>Import</Button>
            <Button variant="outline" onClick={exportCsv} className="h-9 px-3 text-[13px] bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="export-guests-button"><Download className="w-3.5 h-3.5 mr-1.5"/>Export</Button>
            <Button onClick={() => { setForm(initialForm); setOpen(true); }} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-9 px-4 text-[13px]" data-testid="create-guest-button"><Plus className="w-3.5 h-3.5 mr-1.5"/>New guest</Button>
          </div>
        }
      />

      <Card className="p-3 mb-3">
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-3.5 h-3.5 text-hocp-textMuted absolute left-3 top-1/2 -translate-y-1/2"/>
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Guest ID, name, mobile, email, passport…" aria-label="Search guests" className="bg-hocp-bgCard border-hocp-border pl-9 h-9 text-[13px]" data-testid="guests-search"/>
          </div>
          <Select value={vip || "__all__"} onValueChange={(v) => { setVip(v === "__all__" ? "" : v); setPage(1); }}>
            <SelectTrigger className="bg-hocp-bgCard border-hocp-border h-9 text-[13px] w-[140px]" data-testid="guests-vip-filter" aria-label="Filter by VIP status"><SelectValue placeholder="VIP"/></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All VIP</SelectItem>{["none", "bronze", "silver", "gold", "platinum"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={bl || "__all__"} onValueChange={(v) => { setBl(v === "__all__" ? "" : v); setPage(1); }}>
            <SelectTrigger className="bg-hocp-bgCard border-hocp-border h-9 text-[13px] w-[140px]" data-testid="guests-blacklist-filter" aria-label="Filter by blacklist status"><SelectValue placeholder="Blacklist"/></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All</SelectItem><SelectItem value="true">Blacklisted</SelectItem><SelectItem value="false">Not blacklisted</SelectItem></SelectContent>
          </Select>
          <Select value={active || "__all__"} onValueChange={(v) => setActive(v === "__all__" ? "" : v)}>
            <SelectTrigger className="bg-hocp-bgCard border-hocp-border h-9 text-[13px] w-[140px]" data-testid="guests-active-filter" aria-label="Filter by active status"><SelectValue placeholder="Status"/></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All</SelectItem><SelectItem value="true">Active</SelectItem><SelectItem value="false">Inactive</SelectItem></SelectContent>
          </Select>
          <Select value={nat || "__all__"} onValueChange={(v) => setNat(v === "__all__" ? "" : v)}>
            <SelectTrigger className="bg-hocp-bgCard border-hocp-border h-9 text-[13px] w-[160px]" data-testid="guests-nationality-filter" aria-label="Filter by nationality"><SelectValue placeholder="Nationality"/></SelectTrigger>
            <SelectContent className="max-h-[260px]"><SelectItem value="__all__">All nationalities</SelectItem>{Object.entries(lookups.countries || {}).map(([code, name]) => <SelectItem key={code} value={code}>{name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={classification || "__all__"} onValueChange={(v) => setClassification(v === "__all__" ? "" : v)}>
            <SelectTrigger className="bg-hocp-bgCard border-hocp-border h-9 text-[13px] w-[160px]" data-testid="guests-class-filter" aria-label="Filter by classification"><SelectValue placeholder="Classification"/></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All classifications</SelectItem>{Object.entries(lookups.classifications || {}).map(([code, name]) => <SelectItem key={code} value={code}>{name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {selected.size > 0 && (
          <div className="mt-3 pt-3 border-t border-hocp-border flex items-center gap-2 animate-fade-in-up" data-testid="bulk-action-bar">
            <span className="text-[12px] mono text-hocp-textMuted">{selected.size} selected</span>
            <Button size="sm" variant="outline" onClick={bulkExportSelected} className="h-7 px-3 text-[11px] bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="bulk-export"><Download className="w-3 h-3 mr-1.5"/>Export</Button>
            <Button size="sm" variant="outline" onClick={() => setBulkConfirm("activate")} className="h-7 px-3 text-[11px] bg-transparent border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" data-testid="bulk-activate"><Power className="w-3 h-3 mr-1.5"/>Activate</Button>
            <Button size="sm" variant="outline" onClick={() => setBulkConfirm("deactivate")} className="h-7 px-3 text-[11px] bg-transparent border-amber-500/30 text-amber-400 hover:bg-amber-500/10" data-testid="bulk-deactivate"><PowerOff className="w-3 h-3 mr-1.5"/>Deactivate</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="h-7 px-2 text-[11px] text-hocp-textMuted ml-auto">Clear</Button>
          </div>
        )}
      </Card>

      <div className="border border-hocp-border rounded-md bg-hocp-bgCard overflow-hidden" data-testid="guests-table">
        <table className="w-full">
          <thead>
            <tr className="border-b border-hocp-border bg-hocp-bgSubtle">
              <th className="px-3 py-2.5 w-[40px]"><Checkbox checked={selected.size === filteredItems.length && filteredItems.length > 0} onCheckedChange={toggleAll} data-testid="select-all-checkbox"/></th>
              {["Code", "Name", "Contact", "Nationality", "VIP", "Status", "Stays", "Spend"].map(h => <th key={h} className="px-3 py-2.5 text-[10px] font-medium uppercase tracking-[0.1em] text-hocp-textMuted mono text-left">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-xs text-hocp-textMuted">No guests match the filters.</td></tr>
            ) : filteredItems.map(g => (
              <tr key={g.id} className={`border-b border-hocp-border last:border-0 hover:bg-hocp-bgSubtle/70 ${!g.is_active ? "opacity-50" : ""}`} data-testid={`guest-row-${g.code}`}>
                <td className="px-3 py-2.5"><Checkbox checked={selected.has(g.id)} onCheckedChange={() => toggleOne(g.id)} data-testid={`select-${g.code}`}/></td>
                <td className="px-3 py-2.5 text-[12px]"><Link to={`/pms/guests/${g.id}`} className="mono text-hocp-textPrimary hover:text-hocp-deepTeal hover:underline" data-testid={`guest-link-${g.code}`}>{g.code}</Link></td>
                <td className="px-3 py-2.5 text-[13px]">
                  <div className="flex items-center gap-2 text-white">
                    {lookups.titles?.[g.title_code] && <span className="text-hocp-textMuted">{lookups.titles[g.title_code]}</span>}
                    {[g.first_name, g.middle_name, g.last_name].filter(Boolean).join(" ")}
                    {g.is_blacklisted && <ShieldAlert className="w-3 h-3 text-red-400"/>}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-[12px] mono text-hocp-textMuted">{g.primary_phone || "—"}<br/><span className="text-[10px] text-hocp-textMuted">{g.email || "—"}</span></td>
                <td className="px-3 py-2.5 text-[12px] text-hocp-textMuted">{lookups.countries?.[g.nationality_code] || "—"}</td>
                <td className="px-3 py-2.5">{g.vip_status && g.vip_status !== "none" ? <Badge tone="warning"><Star className="w-2.5 h-2.5 mr-1 inline"/>{g.vip_status.toUpperCase()}</Badge> : <span className="text-zinc-700 text-xs">—</span>}</td>
                <td className="px-3 py-2.5"><Badge tone={g.is_active ? "success" : "neutral"}>{g.is_active ? "ACTIVE" : "INACTIVE"}</Badge></td>
                <td className="px-3 py-2.5 text-right mono tabular-nums text-hocp-textPrimary">{g.total_stays || 0}</td>
                <td className="px-3 py-2.5 text-right mono tabular-nums text-hocp-textPrimary">{money(g.total_spend || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-3 text-[11px] mono text-hocp-textMuted">
        <span>{data.total} guests · page {page}/{totalPages} · {filteredItems.length} on this page</span>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="h-7 px-2" data-testid="page-prev"><ChevronLeft className="w-3.5 h-3.5"/></Button>
          <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 px-2" data-testid="page-next"><ChevronRight className="w-3.5 h-3.5"/></Button>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-hocp-bgSubtle border-hocp-border max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">New Guest Profile</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Title</Label>
                <Select value={form.title_code} onValueChange={(v) => setForm({ ...form, title_code: v })}>
                  <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="guest-title"><SelectValue/></SelectTrigger>
                  <SelectContent>{Object.entries(lookups.titles || {}).map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">First name *</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="guest-first-name"/></div>
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Last name</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="guest-last-name"/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Phone</Label>
                <Input value={form.primary_phone} onChange={(e) => setForm({ ...form, primary_phone: e.target.value })} placeholder="+1-555-1234" className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="guest-phone"/></div>
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="guest-email"/></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Gender</Label>
                <Select value={form.gender_code} onValueChange={(v) => setForm({ ...form, gender_code: v })}>
                  <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="guest-gender"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent>{Object.entries(lookups.genders || {}).map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">DOB</Label>
                <DatePicker value={(form.dob || "" || '').slice(0,10)} onChange={(_v) => setForm({ ...form, dob: _v })}/></div>
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Nationality</Label>
                <Select value={form.nationality_code} onValueChange={(v) => setForm({ ...form, nationality_code: v })}>
                  <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="guest-nationality"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent>{Object.entries(lookups.countries || {}).map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Classification</Label>
                <Select value={form.classification_code} onValueChange={(v) => setForm({ ...form, classification_code: v })}>
                  <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="guest-classification"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent>{Object.entries(lookups.classifications || {}).map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">VIP</Label>
                <Select value={form.vip_status} onValueChange={(v) => setForm({ ...form, vip_status: v })}>
                  <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="guest-vip"><SelectValue/></SelectTrigger>
                  <SelectContent>{["none", "bronze", "silver", "gold", "platinum"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* PR2-2 · Inline duplicate detection */}
          <DuplicateGuestPanel
            payload={{
              first_name: form.first_name,
              last_name: form.last_name,
              primary_phone: form.primary_phone,
              email: form.email,
              date_of_birth: form.dob,
            }}
            source="guest_form"
            allowContinueWithExisting={false}
            onDecision={onPanelDecision}
            className="mt-3"
            testid="guest-form-dup-panel"
          />
          <DialogFooter><Button onClick={() => submit({ force: false })} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-guest-button">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 409 fallback dialog (server-side hard block) */}
      <AlertDialog open={!!dupDialog} onOpenChange={(v) => !v && setDupDialog(null)}>
        <AlertDialogContent className="bg-hocp-bgSubtle border-hocp-border">
          <AlertDialogHeader><AlertDialogTitle className="font-heading">Possible duplicate guest</AlertDialogTitle>
            <AlertDialogDescription className="text-hocp-textMuted text-[13px]">{dupDialog?.matches?.length} matching guest(s) found. Use the inline panel above for full options, or force-create.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {dupDialog?.matches?.map(m => (
              <Link key={m.id} to={`/pms/guests/${m.id}`} onClick={() => setDupDialog(null)} className="block p-3 border border-hocp-border rounded hover:bg-hocp-bgSubtle">
                <div className="text-sm text-white">{[m.first_name, m.last_name].filter(Boolean).join(" ")}</div>
                <div className="mono text-[10px] text-hocp-textMuted mt-0.5">{m.code} · {m.primary_phone} · {m.email}</div>
              </Link>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-hocp-border hover:bg-hocp-bgSubtle">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => submit({ force: true, matches: dupDialog?.matches || [], reason: "Server-flagged 409 — reviewed and created anyway" })}
              className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="dup-force-create">
              Create anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
          <DialogHeader><DialogTitle className="font-heading">Import Guests (CSV)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-[11px] text-hocp-textMuted">Required column: first_name. Optional: title_code, last_name, primary_phone, email, nationality_code, classification_code, vip_status, dob, etc.</div>
            <Input type="file" accept=".csv" ref={fileRef} onChange={(e) => setImportFile(e.target.files?.[0])} aria-label="Choose CSV file to import" className="bg-hocp-bgCard border-hocp-border file:text-hocp-textPrimary file:bg-transparent file:border-0 file:text-xs" data-testid="import-file-input"/>
          </div>
          <DialogFooter><Button onClick={importCsv} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-import-button">Import</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk confirm */}
      <AlertDialog open={!!bulkConfirm} onOpenChange={(v) => !v && setBulkConfirm(null)}>
        <AlertDialogContent className="bg-hocp-bgSubtle border-hocp-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{bulkConfirm === "activate" ? "Activate" : "Deactivate"} {selected.size} guests?</AlertDialogTitle>
            <AlertDialogDescription className="text-hocp-textMuted text-[13px]">This action will be audited.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-hocp-border hover:bg-hocp-bgSubtle">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => doBulk(bulkConfirm)} className={bulkConfirm === "activate" ? "bg-emerald-500 hover:bg-emerald-600 text-white" : "bg-amber-500 hover:bg-amber-600 text-black"} data-testid="confirm-bulk">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
